import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import {parse} from 'query-string';
import {push as pushAction} from 'react-router-redux';
import {Card, CardText} from 'material-ui/Card';
import compose from 'recompose/compose';
import {createSelector} from 'reselect';
import inflection from 'inflection';
import getMuiTheme from 'material-ui/styles/getMuiTheme';
import autoprefixer from 'material-ui/utils/autoprefixer';
import {
    SET_SORT,
    SET_PAGE,
    SET_FILTER,
    SORT_DESC,
} from 'admin-on-rest/lib/reducer/admin/resource/list/queryReducer';
import {SET_LIST} from "./fuseConstants";
import ViewTitle from 'admin-on-rest/lib/mui/layout/ViewTitle';
import Title from 'admin-on-rest/lib/mui/layout/Title';
import DefaultPagination from './Pagination';
import DefaultActions from 'admin-on-rest/lib/mui/list/Actions';
import {crudGetList as crudGetListAction} from 'admin-on-rest/lib/actions/dataActions';
import {changeListParams as changeListParamsAction} from 'admin-on-rest/lib/actions/listActions';
import {refreshView as refreshViewAction} from 'admin-on-rest/lib/actions/uiActions';
import translate from 'admin-on-rest/lib/i18n/translate';
import removeKey from 'admin-on-rest/lib/util/removeKey';
import defaultTheme from 'admin-on-rest/lib/mui/defaultTheme';
import withPermissionsFilteredChildren from 'admin-on-rest/lib/auth/withPermissionsFilteredChildren';
import PageDescription from '../components/PageDescription';
import {change, reduxForm} from "redux-form";

const styles = {
    noResults: {padding: 20},
    header: {
        display: 'flex',
        justifyContent: 'space-between',
    },
};

/**
 * List page component (AOR override)
 *
 * The <List> component renders the list layout (title, buttons, filters, pagination),
 * and fetches the list of records from the REST API.
 * It then delegates the rendering of the list of records to its child component.
 * Usually, it's a <Datagrid>, responsible for displaying a table with one row for each post.
 *
 * In Redux terms, <List> is a connected component, and <Datagrid> is a dumb component.
 *
 * Props:
 *   - title
 *   - desc
 *   - perPage
 *   - sort
 *   - filter (the permanent filter to apply to the query)
 *   - actions
 *   - filters (a React Element used to display the filter form)
 *   - pagination
 *
 */
export class List extends Component {
    constructor(props) {
        super(props);
        this.state = {
            listProcessed: false,
        };
    }

    componentDidMount() {
        if (!this.props.requiresFilter) {
            this.getlistData();
        }
        if (Object.keys(this.props.query).length > 0) {
            this.props.changeListParams(this.props.resource, this.props.query);
        }
    }

    componentWillReceiveProps(nextProps) {
        if (
            nextProps.resource !== this.props.resource ||
            nextProps.query.sort !== this.props.query.sort ||
            nextProps.query.order !== this.props.query.order ||
            nextProps.query.page !== this.props.query.page ||
            nextProps.query.filter !== this.props.query.filter ||
            nextProps.filterValues !== this.props.filterValues
        ) {
            // May be removed since data updates are unlikely
            this.updateData(
                Object.keys(nextProps.query).length > 0
                    ? nextProps.query
                    : nextProps.params
            );
        }
        if (
            nextProps.data !== this.props.data ||
            nextProps.ids !== this.props.ids ||
            nextProps.isLoading !== this.props.isLoading
        ) {
            this.setState({
                    listProcessed: false
                },
                () => this.processList(SET_LIST, null));
        }
    }

    shouldComponentUpdate(nextProps, nextState) {
        if (
            nextProps.isLoading === this.props.isLoading &&
            nextProps.width === this.props.width &&
            nextProps.version === this.props.version &&
            nextProps.listData === this.props.listData &&
            nextProps.listIds === this.props.listIds
        ) {
            return false;
        }
        return true;
    }

    getBasePath() {
        return this.props.location.pathname.replace(/\/$/, '');
    }

    /**
     * Merge list params from 3 different sources:
     *   - the query string
     *   - the params stored in the state (from previous navigation)
     *   - the props passed to the List component
     */
    getQuery() {
        const query =
            Object.keys(this.props.query).length > 0
                ? this.props.query
                : {...this.props.params};
        if (!query.sort) {
            query.sort = this.props.sort.field;
            query.order = this.props.sort.order;
        }
        if (!query.perPage) {
            query.perPage = this.props.perPage;
        }
        return query;
    }

    /**
     * Get initial data from resource
     *
     * @param query
     */
    getlistData(query) {
        this.props.dispatch(change('fuse', 'data', {}));
        this.props.dispatch(change('fuse', 'ids', []));
        const params = query || this.getQuery();
        const {sort, order, page, perPage, filter} = params;
        const pagination = {
            page: parseInt(page, 10),
            perPage: parseInt(perPage, 10),
        };
        const permanentFilter = this.props.filter;
        this.props.crudGetList(
            this.props.resource,
            pagination,
            {field: sort, order},
            {...filter, ...permanentFilter}
        );
    }

    // May be removed since data updates are unlikely
    updateData(query) {
        console.log('=> updateData: ', query);
        const params = query || this.getQuery();
        const {sort, order, page, perPage, filter} = params;
        const pagination = {
            page: parseInt(page, 10),
            perPage: parseInt(perPage, 10),
        };
        const permanentFilter = this.props.filter;
        this.props.crudGetList(
            this.props.resource,
            pagination,
            {field: sort, order},
            {...filter, ...permanentFilter}
        );
    }

    // Functions to set and update sort, page and filters
    setFilters = filters => this.changeParams({type: SET_FILTER, payload: filters});
    setSort = sort => this.changeParams({type: SET_SORT, payload: sort});
    setPage = page => this.changeParams({type: SET_PAGE, payload: page});

    showFilter = (filterName, defaultValue) => {
        this.setState({[filterName]: true});
        if (typeof defaultValue !== 'undefined') {
            this.setFilters({
                ...this.props.filterValues,
                [filterName]: defaultValue,
            });
        }
    };

    hideFilter = filterName => {
        this.setState({[filterName]: false});
        const newFilters = removeKey(this.props.filterValues, filterName);
        this.setFilters(newFilters);
    };

    /**
     * Update sort, page and filter parameters from user action
     *
     * @param action
     */
    changeParams(action) {
        this.setState({
            listProcessed: false
        });
        switch (action.type) {
            case SET_FILTER:
                // Update the filter to display
                break;
            case SET_PAGE:
                // Update the current page
                this.processList(SET_PAGE, {page: parseInt(action.payload, 10)});
                break;
            case SET_SORT:
                // Update the field to sort on and switch order if needed
                let newOrder;
                if (this.props.listSortField === action.payload) {
                    newOrder = (this.props.listSortOrder === 'ASC') ? 'DESC' : 'ASC';
                } else {
                    newOrder = 'ASC';
                }
                this.processList(SET_SORT, {field: action.payload, order: newOrder});
                break;
            default:
                break;
        }
    }

    /**
     * Process new list data or update data from sort or pagination updates
     *
     * @param type
     * @param params
     */
    processList(type, params) {
        // Get original received data and ids
        const data = this.props.data;
        const ids = this.props.ids;
        if (!data || !ids) {
            return
        }

        // Set original parameters
        //let filter = (this.props.listFilter) ? this.props.listFilter : this.props.params.filter;
        const sort = {
            field: (this.props.listSortField) ? this.props.listSortField : this.props.sort.field,
            order: (this.props.listSortOrder) ? this.props.listSortOrder : this.props.sort.order
        };
        let page = (this.props.listPage) ? this.props.listPage : this.props.params.page;
        let resetPage = true;

        // Update parameters where applicable
        switch (type) {
            case SET_LIST:
                // Sort and paginate according to original query
                sort.field = this.props.sort.field;
                sort.order = this.props.sort.order;
                page = 1;
                break;
            case SET_FILTER:
                //filter = params.filter;
                // Pending List views requiring a filter change
                break;
            case SET_PAGE:
                // Update page or cancel
                if (params.page === page) {
                    return;
                }
                page = params.page;
                resetPage = false;
                break;
            case SET_SORT:
                // Override the sort with the new parameters
                sort.field = params.field;
                sort.order = params.order;
                break;
            default:
                break;
        }

        let newData, newIds;

        // Filter data and ids into new list
        // (Pending List views requiring a filter change)

        // Sort new data and ids
        let sortedDataAndIds = this.sortDataAndIds(data, sort.field, sort.order);
        newData = sortedDataAndIds.data;
        newIds = sortedDataAndIds.ids;
        newData.fetchedAt = data.fetchedAt;
        newIds.fetchedAt = ids.fetchedAt;

        // Paginate new data and ids according to page and perPage
        const perPage = this.props.perPage;
        if (resetPage === true || page < 1) {
            page = 1;
        }
        if (page > this.numPages(newIds)) {
            page = this.numPages(newIds);
        }
        const offsetEnd = Math.min(page * perPage, this.props.total);
        const offsetBegin = Math.min((page - 1) * perPage + 1, offsetEnd) - 1;
        if (offsetBegin < 0) {
            return;
        }
        const paginatedDataAndIds = this.paginateDataAndIds(newData, newIds, offsetBegin, offsetEnd);
        newData = paginatedDataAndIds.data;
        newIds = paginatedDataAndIds.ids;
        newData.fetchedAt = data.fetchedAt;
        newIds.fetchedAt = ids.fetchedAt;

        // Dispatch store updates to data and parameters
        if (newData !== data) {
            this.props.dispatch(change('fuse', 'data', newData));
        }
        if (newIds !== ids) {
            this.props.dispatch(change('fuse', 'ids', newIds));
        }
        this.props.dispatch(change('fuse', 'sortField', sort.field));
        this.props.dispatch(change('fuse', 'sortOrder', sort.order));
        this.props.dispatch(change('fuse', 'page', page));

        // Processing is complete
        this.setState({
            listProcessed: true
        });
    }

    /**
     * Sort the data object and ids list by requested key and order
     *
     * @param obj
     * @param sortField
     * @param sortOrder
     * @return {{data, ids: Array}}
     */
    sortDataAndIds(obj, sortField, sortOrder) {
        let arr = [];
        for (let key in obj) {
            const sortKey = obj[key][sortField];
            if (key !== 'undefined' && sortKey !== 'undefined') {
                arr.push({key: key, sortKey: sortKey});
            }
        }
        arr.sort(
            function (a, b) {
                return a.sortKey.toString().toLowerCase().localeCompare(b.sortKey.toString().toLowerCase());
            }
        );
        let sortedData = {};
        let sortedIds = [];
        let i;
        if (sortOrder === 'DESC') {
            for (i = arr.length - 1; i >= 0; i--) {
                sortedData[arr[i].key] = obj[arr[i].key];
                sortedIds.push(arr[i].key);
            }
        } else {
            for (i = 0; i < arr.length; i++) {
                sortedData[arr[i].key] = obj[arr[i].key];
                sortedIds.push(arr[i].key);
            }
        }

        return {
            data: sortedData,
            ids: sortedIds
        }
    }

    /**
     * Select data and ids based on pagination settings
     *
     * @param data
     * @param ids
     * @param start
     * @param end
     * @return {{data, ids: Array}}
     */
    paginateDataAndIds(data, ids, start, end) {
        let paginatedData = {};
        let paginatedIds = [];
        for (let i = start; i < end; i++) {
            paginatedData[ids[i]] = data[ids[i]];
            paginatedIds.push(ids[i]);
        }
        return {
            data: paginatedData,
            ids: paginatedIds
        }
    }

    /**
     * Get total of pages
     *
     * @param list
     * @return {number}
     */
    numPages(list) {
        return Math.ceil(list.length / this.props.perPage);
    }

    refresh = () => {
        if (process.env !== 'production') {
            console.warn( // eslint-disable-line
                'Deprecation warning: The preferred way to refresh the List view is to connect your custom button with redux and dispatch the `refreshView` action.'
            );
        }
        this.props.refreshView();
    };

    render() {
        const {
            children,
            filters,
            pagination = <DefaultPagination/>,
            actions = <DefaultActions/>,
            resource,
            hasCreate,
            title,
            desc,
            isLoading,
            total,
            listData,
            listIds,
            listPage,
            listSortField,
            listSortOrder,
            translate,
            theme,
            version,
        } = this.props;
        const {listProcessed} = this.state;
        const query = this.getQuery();
        const filterValues = query.filter;
        const basePath = this.getBasePath();

        const resourceName = translate(`resources.${resource}.name`, {
            smart_count: 2,
            _: inflection.humanize(inflection.pluralize(resource)),
        });
        const defaultTitle = translate('aor.page.list', {
            name: `${resourceName}`,
        });
        const titleElement = (
            <Title title={title} defaultTitle={defaultTitle}/>
        );
        const descriptionElement = desc ? (
            <PageDescription desc={desc}/>
        ) : (
            ''
        );
        const muiTheme = getMuiTheme(theme);
        const prefix = autoprefixer(muiTheme);

        return (
            <div className="list-page">
                <Card style={{opacity: isLoading ? 0.8 : 1}}>
                    <div style={prefix(styles.header)}>
                        <ViewTitle title={titleElement}/>
                        {actions &&
                        React.cloneElement(actions, {
                            resource,
                            filters,
                            filterValues,
                            basePath,
                            hasCreate,
                            displayedFilters: this.state,
                            showFilter: this.showFilter,
                            theme,
                            refresh: this.refresh,
                        })}
                    </div>
                    {descriptionElement}
                    {filters &&
                    React.cloneElement(filters, {
                        resource,
                        hideFilter: this.hideFilter,
                        filterValues,
                        displayedFilters: this.state,
                        setFilters: this.setFilters,
                        context: 'form',
                    })}
                    {isLoading || total > 0 ? (
                        <div key={version}>
                            {children && listProcessed &&
                            React.cloneElement(children, {
                                resource,
                                ids: listIds,
                                data: listData,
                                currentSort: {
                                    field: listSortField,
                                    order: listSortOrder
                                },
                                basePath,
                                isLoading,
                                setSort: this.setSort,
                            })}
                            {pagination &&
                            React.cloneElement(pagination, {
                                total: total,
                                page: parseInt(listPage, 10),
                                perPage: parseInt(query.perPage, 10),
                                setPage: this.setPage,
                            })}
                        </div>
                    ) : (
                        <CardText style={styles.noResults}>
                            {translate('aor.navigation.no_results')}
                        </CardText>
                    )}
                </Card>
            </div>
        );
    }
}

List.propTypes = {
    // the props you can change
    title: PropTypes.any,
    desc: PropTypes.any,
    filter: PropTypes.object,
    filters: PropTypes.element,
    pagination: PropTypes.element,
    actions: PropTypes.element,
    perPage: PropTypes.number.isRequired,
    sort: PropTypes.shape({
        field: PropTypes.string,
        order: PropTypes.string,
    }),
    children: PropTypes.node,
    listData: PropTypes.object,
    listIds: PropTypes.array,
    listFilter: PropTypes.object,
    listPage: PropTypes.number,
    listSortField: PropTypes.string,
    listSortOrder: PropTypes.string,
    // the props managed by admin-on-rest
    authClient: PropTypes.func,
    changeListParams: PropTypes.func.isRequired,
    crudGetList: PropTypes.func.isRequired,
    data: PropTypes.object, // eslint-disable-line react/forbid-prop-types
    filterValues: PropTypes.object, // eslint-disable-line react/forbid-prop-types
    hasCreate: PropTypes.bool.isRequired,
    ids: PropTypes.array,
    isLoading: PropTypes.bool.isRequired,
    location: PropTypes.object.isRequired,
    path: PropTypes.string,
    params: PropTypes.object.isRequired,
    push: PropTypes.func.isRequired,
    query: PropTypes.object.isRequired,
    requiresFilter: PropTypes.bool,
    resource: PropTypes.string.isRequired,
    refreshView: PropTypes.func.isRequired,
    total: PropTypes.number.isRequired,
    translate: PropTypes.func.isRequired,
    theme: PropTypes.object.isRequired,
    version: PropTypes.number.isRequired,
};

List.defaultProps = {
    filter: {},
    filterValues: {},
    perPage: 10,
    requiresFilter: false,
    sort: {
        field: 'id',
        order: SORT_DESC,
    },
    listFilter: {},
    listPage: 1,
    listSortField: 'id',
    listSortOrder: SORT_DESC,
    theme: defaultTheme,
};

const getLocationSearch = props => props.location.search;
const getQuery = createSelector(getLocationSearch, locationSearch => {
    const query = parse(locationSearch);
    if (query.filter && typeof query.filter === 'string') {
        query.filter = JSON.parse(query.filter);
    }
    return query;
});

function mapStateToProps(state, props) {
    const resource = state.admin.resources[props.resource];
    const form = state.form.fuse;
    const propObject = {
        query: getQuery(props),
        params: resource.list.params,
        ids: resource.list.ids,
        total: resource.list.total,
        data: resource.data,
        isLoading: state.admin.loading > 0,
        filterValues: resource.list.params.filter,
        listData: resource.data,
        listIds: resource.list.ids,
        listPage: resource.list.params.page,
        version: state.admin.ui.viewVersion
    };
    // Update processed props from store changes
    if (form && form.values) {
        const formValues = form.values;
        propObject.listData = (formValues.data) ? formValues.data : resource.data;
        propObject.listIds = (formValues.ids) ? formValues.ids : resource.list.ids;
        propObject.listPage = (formValues.page) ? formValues.page : resource.list.params.page;
        propObject.listFilter = (formValues.filter) ? formValues.filter : resource.list.params.filter;
        propObject.listSortField = (formValues.sortField) ? formValues.sortField : props.sort.field;
        propObject.listSortOrder = (formValues.sortOrder) ? formValues.sortOrder : props.sort.order;
    }
    return propObject;
}

const enhance = compose(
    connect(mapStateToProps, {
        crudGetList: crudGetListAction,
        changeListParams: changeListParamsAction,
        push: pushAction,
        refreshView: refreshViewAction,
    }),
    reduxForm({
        form: 'fuse',
        enableReinitialize: true,
    }),
    translate,
    withPermissionsFilteredChildren
);

export default enhance(List);



// WEBPACK FOOTER //
// ./src/Fuse/admin/ListStatic.js